import { useState } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";

export const isDirty = (a, b) => JSON.stringify(a ?? {}) !== JSON.stringify(b ?? {});

// Captures `value` at the moment the dialog opens, for forms initialised synchronously.
export const useOpenSnapshot = (open, value) => {
  const [snap, setSnap] = useState(value);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSnap(value);
  }
  return open && !wasOpen ? value : snap;
};

export const DiscardConfirm = ({ open, onKeep, onDiscard }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onKeep()}>
    <AlertDialogContent className="bg-zinc-900 border-white/10" data-testid="discard-changes-confirm">
      <AlertDialogHeader>
        <AlertDialogTitle>Discard unsaved changes?</AlertDialogTitle>
        <AlertDialogDescription className="text-zinc-400">You have changes that haven't been saved. If you leave now they will be lost.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="discard-keep-editing" className="bg-transparent border-white/15">Keep editing</AlertDialogCancel>
        <AlertDialogAction data-testid="discard-confirm-button" onClick={onDiscard} className="bg-red-600 hover:bg-red-500">Discard changes</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

// Data-entry dialog: ignores outside clicks; X / Esc / <DialogClose> confirm before discarding unsaved changes.
export const GuardedDialog = ({ open, onOpenChange, dirty, onDiscard, blockOutside = true, className, testid, children }) => {
  const [confirm, setConfirm] = useState(false);
  const handle = (o) => {
    if (o) return onOpenChange(true);
    if (dirty) setConfirm(true);
    else onOpenChange(false);
  };
  const discard = () => { setConfirm(false); onDiscard && onDiscard(); onOpenChange(false); };
  return (
    <>
      <Dialog open={open} onOpenChange={handle}>
        <DialogContent
          className={className}
          data-testid={testid}
          onInteractOutside={(e) => blockOutside && e.preventDefault()}
        >
          {children}
        </DialogContent>
      </Dialog>
      <DiscardConfirm open={confirm} onKeep={() => setConfirm(false)} onDiscard={discard} />
    </>
  );
};
